import React, { useRef } from 'react'
import type ReactPlayer from 'react-player'
import { AnyFunction } from '@codeleap/common'
import { variantProvider } from '@/app'
import { Modal, Player } from '@/components'
import { VideoSubtitleOverlay } from '@/components/Translations/VideoSubtitleOverlay'
import type { Video } from '@/types/video'

type PlayerFullscreenProps = {
  visible: boolean
  toggle: AnyFunction
  /**
   * The whole payload, not just its URL: the subtitle tracks travel with it, and nothing
   * discovers them from the stream — Bunny's HLS manifest declares `CLOSED-CAPTIONS=NONE`.
   */
  video: Video
}

export const PlayerFullscreen = (props: PlayerFullscreenProps) => {
  const { visible, toggle, video } = props

  // `Player` forwards its `ReactPlayer` through `useImperativeHandle`, which is the only way
  // to read media position here: `Player` assigns its own `onProgress` after spreading the
  // caller's props, so a handler passed in from outside is discarded.
  const playerRef = useRef<ReactPlayer>(null)

  return (
    <Modal
      visible={visible}
      toggle={toggle}
      closable={false}
      variants={['fullscreen', 'center', 'player']}
      debugName="Modal fullscreen player"
      showClose={false}
    >
      <Player ref={playerRef} url={video?.url} paused={!visible} toggleFullscreen={toggle} onClose={toggle} />

      <VideoSubtitleOverlay
        debugName="Lesson video subtitles"
        video={video}
        getCurrentTime={() => playerRef.current?.getCurrentTime()}
        // A closed modal keeps this mounted; there is no cue to track until it opens.
        enabled={visible}
        style={styles.subtitles}
      />
    </Modal>
  )
}

const styles = variantProvider.createComponentStyle(
  theme => ({
    subtitles: {
      position: 'absolute',
      bottom: theme.spacing.value(10),
      left: theme.spacing.value(2),
      right: theme.spacing.value(2),
      zIndex: 4,
      width: 'auto'
    }
  }),
  true
)
